import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, query, where, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { QueueTicket, Barber } from '../../types';
import {
 LineChart,
 Line,
 BarChart,
 Bar,
 PieChart,
 Pie,
 Cell,
 XAxis,
 YAxis,
 CartesianGrid,
 Tooltip,
 Legend,
 ResponsiveContainer,
} from 'recharts';

interface DateRange {
 start: Date;
 end: Date;
}

interface TicketsByHour {
 hour: string;
 created: number;
 completed: number;
}

interface TicketsByStatus {
 name: string;
 value: number;
 color: string;
}

interface BarberPerformance {
 barberName: string;
 completed: number;
 avgServiceTime: number;
 noShows: number;
}

interface Props {
 selectedFranchiseId: string;
 selectedBranchId: string;
}

const AnalyticsDashboard: React.FC<Props> = ({ selectedFranchiseId, selectedBranchId }) => {
 const { t } = useTranslation();
 const [loading, setLoading] = useState(true);
 const [dateRange, setDateRange] = useState<DateRange>({
 start: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), // Last 7 days
 end: new Date(),
 });
 const [ticketsByHour, setTicketsByHour] = useState<TicketsByHour[]>([]);
 const [ticketsByStatus, setTicketsByStatus] = useState<TicketsByStatus[]>([]);
 const [barberPerformance, setBarberPerformance] = useState<BarberPerformance[]>([]);
 const [metrics, setMetrics] = useState({
 totalTickets: 0,
 completedTickets: 0,
 completionRate: 0,
 noShowRate: 0,
 cancellationRate: 0,
 avgWaitTime: 0,
 });

 const loadAnalytics = useCallback(async (
 franchiseId: string,
 branchId: string,
 range: DateRange
 ) => {
 setLoading(true);

 try {
 // Query tickets in date range
 let ticketsQuery = query(
 collection(db, 'queues'),
 where('createdAt', '>=', Timestamp.fromDate(range.start)),
 where('createdAt', '<=', Timestamp.fromDate(range.end))
 );

 if (franchiseId !== 'all') {
 ticketsQuery = query(ticketsQuery, where('franchiseId', '==', franchiseId));
 }

 if (branchId !== 'all') {
 ticketsQuery = query(ticketsQuery, where('branchId', '==', branchId));
 }

 const ticketsSnapshot = await getDocs(ticketsQuery);
 const tickets = ticketsSnapshot.docs.map((doc) => ({
 queueId: doc.id,
 ...doc.data(),
 })) as QueueTicket[];

 // Calculate metrics
 const totalTickets = tickets.length;
 const completedTickets = tickets.filter((t) => t.status === 'completed').length;
 const noShowTickets = tickets.filter((t) => t.status === 'expired').length;
 const cancelledTickets = tickets.filter((t) => t.status === 'cancelled').length;

 const completionRate = totalTickets > 0 ? (completedTickets / totalTickets) * 100 : 0;
 const noShowRate = totalTickets > 0 ? (noShowTickets / totalTickets) * 100 : 0;
 const cancellationRate = totalTickets > 0 ? (cancelledTickets / totalTickets) * 100 : 0;

 const totalWaitTime = tickets.reduce((sum, t) => sum + (t.estimatedWaitTime || 0), 0);
 const avgWaitTime = totalTickets > 0 ? totalWaitTime / totalTickets : 0;

 setMetrics({
 totalTickets,
 completedTickets,
 completionRate,
 noShowRate,
 cancellationRate,
 avgWaitTime,
 });

 // Tickets by hour
 const hourlyData: { [key: string]: { created: number; completed: number } } = {};
 for (let i = 0; i < 24; i++) {
 const hour = i.toString().padStart(2, '0') + ':00';
 hourlyData[hour] = { created: 0, completed: 0 };
 }

 tickets.forEach((ticket) => {
 const hour =
 ticket.createdAt.toDate().getHours().toString().padStart(2, '0') + ':00';
 hourlyData[hour].created++;
 if (ticket.status === 'completed') {
 hourlyData[hour].completed++;
 }
 });

 const hourlyArray = Object.entries(hourlyData).map(([hour, data]) => ({
 hour,
 created: data.created,
 completed: data.completed,
 }));

 setTicketsByHour(hourlyArray);

 // Tickets by status
 const statusData: TicketsByStatus[] = [
 {
 name: t('analytics.statusLabels.completed'),
 value: completedTickets,
 color: '#22C55E',
 },
 {
 name: t('analytics.statusLabels.noShow'),
 value: noShowTickets,
 color: '#ef4444',
 },
 {
 name: t('analytics.statusLabels.cancelled'),
 value: cancelledTickets,
 color: '#f59e0b',
 },
 {
 name: t('analytics.statusLabels.active'),
 value: tickets.filter(
 (ticket) =>
 ticket.status === 'waiting' ||
 ticket.status === 'notified' ||
 ticket.status === 'arrived' ||
 ticket.status === 'in_service'
 ).length,
 color: '#3b82f6',
 },
 ];

 setTicketsByStatus(statusData.filter((s) => s.value > 0));

 // Barber performance
 let barbersQuery = query(collection(db, 'barbers'));

 if (franchiseId !== 'all') {
 barbersQuery = query(barbersQuery, where('franchiseId', '==', franchiseId));
 }

 if (branchId !== 'all') {
 barbersQuery = query(barbersQuery, where('branchId', '==', branchId));
 }

 const barbersSnapshot = await getDocs(barbersQuery);
 const barbers = barbersSnapshot.docs.map((doc) => ({
 barberId: doc.id,
 ...doc.data(),
 })) as Barber[];

 const barberStats: BarberPerformance[] = barbers.map((barber) => {
 const barberTickets = tickets.filter((t) => t.barberId === barber.barberId);
 const completed = barberTickets.filter((t) => t.status === 'completed').length;
 const noShows = barberTickets.filter((t) => t.status === 'expired').length;

 const totalServiceTime = barberTickets
 .filter((t) => t.serviceStartedAt && t.completedAt)
 .reduce((sum, t) => {
 if (t.serviceStartedAt && t.completedAt) {
 const duration =
 t.completedAt.toDate().getTime() - t.serviceStartedAt.toDate().getTime();
 return sum + duration / (1000 * 60); // Convert to minutes
 }
 return sum;
 }, 0);

 const avgServiceTime = completed > 0 ? totalServiceTime / completed : 0;

 return {
 barberName: barber.displayName,
 completed,
 avgServiceTime: Math.round(avgServiceTime),
 noShows,
 };
 });

 setBarberPerformance(barberStats.sort((a, b) => b.completed - a.completed));
 } catch (error) {
 console.error('Error loading analytics:', error);
 } finally {
 setLoading(false);
 }
 }, [t]);

 // Effect to load analytics when filters change
 useEffect(() => {
 loadAnalytics(selectedFranchiseId, selectedBranchId, dateRange);
 }, [loadAnalytics, selectedFranchiseId, selectedBranchId, dateRange]);

 const handleDateRangeChange = (days: number) => {
 setDateRange({
 start: new Date(Date.now() - days * 24 * 60 * 60 * 1000),
 end: new Date(),
 });
 };

 const exportToCSV = () => {
 const csv = [
 ['Metric', 'Value'],
 ['Total Tickets', metrics.totalTickets],
 ['Completed Tickets', metrics.completedTickets],
 ['Completion Rate', `${metrics.completionRate.toFixed(2)}%`],
 ['No-Show Rate', `${metrics.noShowRate.toFixed(2)}%`],
 ['Cancellation Rate', `${metrics.cancellationRate.toFixed(2)}%`],
 ['Average Wait Time', `${metrics.avgWaitTime.toFixed(0)} min`],
 [],
 ['Barber Performance'],
 ['Barber', 'Completed', 'Avg Service Time', 'No-Shows'],
 ...barberPerformance.map((b) => [
 b.barberName,
 b.completed,
 `${b.avgServiceTime} min`,
 b.noShows,
 ]),
 ]
 .map((row) => row.join(','))
 .join('\n');

 const blob = new Blob([csv], { type: 'text/csv' });
 const url = window.URL.createObjectURL(blob);
 const a = document.createElement('a');
 a.href = url;
 a.download = `analytics-${dateRange.start.toISOString().split('T')[0]}-${
 dateRange.end.toISOString().split('T')[0]
 }.csv`;
 a.click();
 };

 if (loading) {
 return (
 <div className="flex items-center justify-center h-96">
 <div className="text-center">
 <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-info/30 mx-auto"></div>
 <p className="mt-4 text-ink-muted">{t('analytics.loading')}</p>
 </div>
 </div>
 );
 }

 return (
 <div className="p-6 space-y-6">
 {/* Header */}
 <div className="flex items-center justify-between flex-wrap gap-4">
 <div>
 <h2 className="text-2xl font-bold text-ink">{t('analytics.title')}</h2>
 <p className="text-sm text-ink-muted mt-1 text-ink-muted">
 {t('analytics.subtitle')}
 </p>
 </div>
 <div className="flex items-center space-x-2 flex-wrap gap-2">
 <button
 onClick={() => handleDateRangeChange(7)}
 className="px-3 py-2 text-sm font-medium text-ink-muted bg-surface-2 rounded-lg hover:bg-surface-2 text-ink-muted"
 >
 {t('analytics.dateRange.last7Days')}
 </button>
 <button
 onClick={() => handleDateRangeChange(30)}
 className="px-3 py-2 text-sm font-medium text-ink-muted bg-surface-2 rounded-lg hover:bg-surface-2 text-ink-muted"
 >
 {t('analytics.dateRange.last30Days')}
 </button>
 <button
 onClick={() => handleDateRangeChange(90)}
 className="px-3 py-2 text-sm font-medium text-ink-muted bg-surface-2 rounded-lg hover:bg-surface-2 text-ink-muted"
 >
 {t('analytics.dateRange.last90Days')}
 </button>
 <button
 onClick={exportToCSV}
 className="px-3 py-2 text-sm font-medium text-white bg-info rounded-lg hover:bg-info/90"
 >
 {t('analytics.exportCSV')}
 </button>
 </div>
 </div>

 {/* Key Metrics */}
 <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
 <div className="bg-white border border-hairline rounded-lg p-4 bg-surface-2 border-hairline">
 <p className="text-xs text-ink-muted">{t('analytics.metrics.totalTickets')}</p>
 <p className="text-2xl font-bold text-ink mt-1">{metrics.totalTickets}</p>
 </div>
 <div className="bg-white border border-hairline rounded-lg p-4 bg-surface-2 border-hairline">
 <p className="text-xs text-ink-muted">{t('analytics.metrics.completed')}</p>
 <p className="text-2xl font-bold text-success mt-1">{metrics.completedTickets}</p>
 </div>
 <div className="bg-white border border-hairline rounded-lg p-4 bg-surface-2 border-hairline">
 <p className="text-xs text-ink-muted">{t('analytics.metrics.completionRate')}</p>
 <p className="text-2xl font-bold text-info mt-1">
 {metrics.completionRate.toFixed(1)}%
 </p>
 </div>
 <div className="bg-white border border-hairline rounded-lg p-4 bg-surface-2 border-hairline">
 <p className="text-xs text-ink-muted">{t('analytics.metrics.noShowRate')}</p>
 <p className="text-2xl font-bold text-danger mt-1">
 {metrics.noShowRate.toFixed(1)}%
 </p>
 </div>
 <div className="bg-white border border-hairline rounded-lg p-4 bg-surface-2 border-hairline">
 <p className="text-xs text-ink-muted">{t('analytics.metrics.cancelRate')}</p>
 <p className="text-2xl font-bold text-accent mt-1">
 {metrics.cancellationRate.toFixed(1)}%
 </p>
 </div>
 <div className="bg-white border border-hairline rounded-lg p-4 bg-surface-2 border-hairline">
 <p className="text-xs text-ink-muted">{t('analytics.metrics.avgWaitTime')}</p>
 <p className="text-2xl font-bold text-accent mt-1">
 {metrics.avgWaitTime.toFixed(0)}m
 </p>
 </div>
 </div>

 {/* Charts */}
 <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
 {/* Tickets by Hour */}
 <div className="bg-white border border-hairline rounded-lg p-6 bg-surface-2 border-hairline">
 <h3 className="text-lg font-semibold text-ink mb-4">{t('analytics.charts.ticketsByHour')}</h3>
 <ResponsiveContainer width="100%" height={300}>
 <LineChart data={ticketsByHour}>
 <CartesianGrid strokeDasharray="3 3" />
 <XAxis dataKey="hour" />
 <YAxis />
 <Tooltip />
 <Legend />
 <Line
 type="monotone"
 dataKey="created"
 stroke="#3b82f6"
 strokeWidth={2}
 name={t('analytics.chartLabels.created')}
 />
 <Line
 type="monotone"
 dataKey="completed"
 stroke="#22C55E"
 strokeWidth={2}
 name={t('analytics.chartLabels.completed')}
 />
 </LineChart>
 </ResponsiveContainer>
 </div>

 {/* Tickets by Status */}
 <div className="bg-white border border-hairline rounded-lg p-6 bg-surface-2 border-hairline">
 <h3 className="text-lg font-semibold text-ink mb-4">{t('analytics.charts.ticketsByStatus')}</h3>
 <ResponsiveContainer width="100%" height={300}>
 <PieChart>
 <Pie
 data={ticketsByStatus as any}
 cx="50%"
 cy="50%"
 labelLine={false}
 label={({ name, percent }) => `${name}: ${((percent || 0) * 100).toFixed(0)}%`}
 outerRadius={100}
 fill="#FACC15"
 dataKey="value"
 >
 {ticketsByStatus.map((entry, index) => (
 <Cell key={`cell-${index}`} fill={entry.color} />
 ))}
 </Pie>
 <Tooltip />
 </PieChart>
 </ResponsiveContainer>
 </div>
 </div>

 {/* Barber Performance */}
 <div className="bg-white border border-hairline rounded-lg p-6 bg-surface-2 border-hairline">
 <h3 className="text-lg font-semibold text-ink mb-4">{t('analytics.charts.barberPerformance')}</h3>
 <ResponsiveContainer width="100%" height={400}>
 <BarChart data={barberPerformance}>
 <CartesianGrid strokeDasharray="3 3" />
 <XAxis dataKey="barberName" />
 <YAxis />
 <Tooltip />
 <Legend />
 <Bar dataKey="completed" fill="#22C55E" name={t('analytics.chartLabels.completedServices')} />
 <Bar dataKey="avgServiceTime" fill="#3b82f6" name={t('analytics.chartLabels.avgServiceTimeMin')} />
 <Bar dataKey="noShows" fill="#ef4444" name={t('analytics.chartLabels.noShows')} />
 </BarChart>
 </ResponsiveContainer>
 </div>

 {/* Barber Performance Table */}
 <div className="bg-white border border-hairline rounded-lg p-6 bg-surface-2 border-hairline">
 <h3 className="text-lg font-semibold text-ink mb-4">{t('analytics.barberTable.title')}</h3>
 <div className="overflow-x-auto">
 <table className="min-w-full divide-y divide-hairline">
 <thead className="bg-surface-2">
 <tr>
 <th className="px-6 py-3 text-left text-xs font-medium text-ink-subtle uppercase tracking-wider text-ink-muted">
 {t('analytics.barberTable.barber')}
 </th>
 <th className="px-6 py-3 text-left text-xs font-medium text-ink-subtle uppercase tracking-wider text-ink-muted">
 {t('analytics.barberTable.completed')}
 </th>
 <th className="px-6 py-3 text-left text-xs font-medium text-ink-subtle uppercase tracking-wider text-ink-muted">
 {t('analytics.barberTable.avgServiceTime')}
 </th>
 <th className="px-6 py-3 text-left text-xs font-medium text-ink-subtle uppercase tracking-wider text-ink-muted">
 {t('analytics.barberTable.noShows')}
 </th>
 <th className="px-6 py-3 text-left text-xs font-medium text-ink-subtle uppercase tracking-wider text-ink-muted">
 {t('analytics.barberTable.successRate')}
 </th>
 </tr>
 </thead>
 <tbody className="bg-white divide-y divide-hairline bg-surface-2">
 {barberPerformance.map((barber, index) => {
 const total = barber.completed + barber.noShows;
 const successRate = total > 0 ? (barber.completed / total) * 100 : 0;

 return (
 <tr key={index}>
 <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-ink">
 {barber.barberName}
 </td>
 <td className="px-6 py-4 whitespace-nowrap text-sm text-ink">
 {barber.completed}
 </td>
 <td className="px-6 py-4 whitespace-nowrap text-sm text-ink">
 {barber.avgServiceTime} min
 </td>
 <td className="px-6 py-4 whitespace-nowrap text-sm text-ink">
 {barber.noShows}
 </td>
 <td className="px-6 py-4 whitespace-nowrap text-sm text-ink">
 <span
 className={`px-2 py-1 text-xs font-medium rounded-full ${
 successRate >= 90
 ? 'bg-success/10 text-success'
 : successRate >= 75
 ? 'bg-accent/10 text-accent'
 : 'bg-danger/10 text-danger'
 }`}
 >
 {successRate.toFixed(1)}%
 </span>
 </td>
 </tr>
 );
 })}
 </tbody>
 </table>
 </div>
 </div>
 </div>
 );
};

export default AnalyticsDashboard;
